import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { ProductTypeLabel } from "app/components/ProductTypeLabel";
import { Package } from "app/components/PublicProfiles/services/api";
import {
  isAlreadySubscribed,
  packageCtaKey,
} from "app/components/PublicProfiles/services/purchasability";
import { cls } from "app/utils";
import { generateCurrencySymbol } from "app/routes/BuildProgram/Pricing/utils";
import { scheduledFirstChargeNote } from "app/routes/Billing/NewOffering/utils";
import sessionIcon from "app/components/PublicProfiles/assets/session.svg";

import styles from "./PackageCard.module.scss";

interface Props {
  pkg: Package;
  /** Opens the details modal — from "Read more...", or from the card's CTA. */
  onOpenDetails: () => void;
}

/** "3 Sessions", or ∞ when a rate link is unlimited. Null when there are none. */
const sessionCount = (pkg: Package) => {
  const rateLinks = pkg.rateLinks ?? [];
  if (rateLinks.length === 0) {
    return null;
  }
  if (rateLinks.length === 1) {
    return rateLinks[0].quantity ?? null;
  }
  const total = rateLinks.reduce((sum, link) => sum + (link.quantity ?? 0), 0);
  return total === 0 ? null : total;
};

/**
 * Which payment line the card shows, as an i18n key plus its interpolation, or
 * null where the payload does not say.
 *
 * A subscription whose `recurring` is missing used to fall through to the "every
 * N months" line with N of 0 — "Billed every 0 months" under the price. Nothing
 * is better than a wrong interval.
 */
const paymentKey = (pkg: Package): [string, Record<string, unknown>?] | null => {
  if (pkg.productType === "product") {
    return ["One-time payment"];
  }
  if (pkg.recurring === "1") {
    return ["Billed monthly"];
  }
  if (pkg.recurring === "12") {
    return ["Billed annually"];
  }

  const months = Number(pkg.recurring);
  if (!Number.isFinite(months) || months <= 0) {
    return null;
  }
  return ["Billed every {{count}} months", { count: months }];
};

/**
 * A package as the coach-page frame draws it: the type badge and the session
 * count on one row, then the title, the description, a rule, and the price with
 * its button beside it.
 *
 * The public profile's own card puts the button full-width under the price and
 * carries a "Details" pill, which is that page's layout rather than this frame's
 * — hence a card here. The behaviour is the same though: the description clamps,
 * "Read more..." appears only when it actually overflows, and both it and the
 * button open the full details.
 */
const PackageCard: React.FC<Props> = ({ pkg, onOpenDetails }) => {
  const { t } = useTranslation();
  const descriptionRef = useRef<HTMLParagraphElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const measure = () => {
      const el = descriptionRef.current;
      if (el) {
        setIsTruncated(el.scrollHeight > el.clientHeight + 1);
      }
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [pkg.description]);

  const sessions = sessionCount(pkg);
  // CFTB-3445: a subscription the athlete already holds cannot be bought again —
  // the checkout answers 422 — so its button says "Subscribed" and does nothing.
  // "Read more..." still opens the details, which is the only thing left to do
  // with it here; managing the subscription lives in the account's own list.
  const subscribed = isAlreadySubscribed(pkg);
  // CN-2832: a custom start date bills nothing at checkout.
  const firstChargeNote = scheduledFirstChargeNote(
    pkg,
    `${generateCurrencySymbol(pkg.currency)}${pkg.price}`,
    t,
  );

  return (
    <div className={styles.card}>
      <div className={styles.head}>
        <ProductTypeLabel product={pkg} enabledIntegration={true} />
        <span className={styles.sessions}>
          <img src={sessionIcon} alt="" className={styles.sessions_icon} />
          {sessions === null ? (
            <span className={styles.unlimited}>&infin;</span>
          ) : (
            sessions
          )}{" "}
          {t("Sessions")}
        </span>
      </div>

      <h3 className={styles.title}>{pkg.title}</h3>

      <div className={styles.description_wrap}>
        <p ref={descriptionRef} className={styles.description}>
          {pkg.description}
        </p>
        {isTruncated && (
          <button
            type="button"
            className={styles.read_more}
            onClick={onOpenDetails}
            data-testid="package-read-more"
          >
            {t("Read more...")}
          </button>
        )}
      </div>

      <hr className={styles.divider} />

      <div className={styles.footer}>
        <div className={styles.price_block}>
          <span className={styles.price}>
            {generateCurrencySymbol(pkg.currency)}
            {pkg.price}
          </span>
          {paymentKey(pkg) && (
            <span className={styles.payment_info}>
              {t(...(paymentKey(pkg) as [string, Record<string, unknown>]))}
            </span>
          )}
          {firstChargeNote && (
            <span className={styles.payment_info}>{firstChargeNote}</span>
          )}
        </div>
        <button
          type="button"
          className={cls(styles.cta, {
            [styles.cta_disabled]: subscribed,
          })}
          disabled={subscribed}
          onClick={onOpenDetails}
          data-testid="package-cta"
        >
          {t(packageCtaKey(pkg))}
        </button>
      </div>
    </div>
  );
};

export default PackageCard;
